import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  MenuItem,
  Skeleton,
  Pagination,
  FormControl,
  Select,
  Grid,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ErrorPage from "../Components/ErrorPage";
import CurrencyAdd from "./CurrencyAdd";
import CurrencyEdit from "./CurrencyEdit";
import useFetchObjects from "../../api/useFetchObjects";
import useDelete from "../../api/useDelete";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";

const CurrencyIndex = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editData, setEditData] = useState(null);

  const { data, isLoading, isError, refetch } = useFetchObjects(
    ["currency", searchTerm, page, rowsPerPage],
    `shop/currencies/?search=${searchTerm}&page=${page}&page_size=${rowsPerPage}`,
    token
  );

  useEffect(() => {
    refetch();
  }, [searchTerm, page, rowsPerPage, refetch]);
  const { handleDelete, ConfirmDialog } = useDelete("shop/currencies", token);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setPage(1);
  };

  const currencies = data && data.results ? data.results : data || [];
  const totalCount = data?.count || (Array.isArray(currencies) ? currencies.length : 0);

  if (isError) {
    return <ErrorPage message={t("currency.failedToLoadCurrencies")} />;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h4">{t("currency.currencyList")}</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setOpenAdd(true)}
        >
          {t("currency.addNewCurrency")}
        </Button>
      </Box>
      <Typography variant="subtitle1" color="textSecondary" mb={2}>
        {t("currency.dataLoadedOn")} {new Date().toLocaleString()}
      </Typography>
      {/* Search Field */}
      <Box sx={{ mb: 2 }}>
        <TextField
          variant="outlined"
          fullWidth
          placeholder={t("currency.searchCurrencies")}
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      {/* Currency Table */}
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table aria-label="currency table">
          <TableHead>
            <TableRow sx={{ backgroundColor: "primary.main" }}>
              <TableCell sx={{ color: "white" }}>
                {t("currency.index")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("currency.title")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("currency.shop", "Branch")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("currency.description")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("currency.action")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? Array.from(new Array(5)).map((_, i) => (
                  <TableRow key={`skeleton-${i}`}>
                    {["Index", "Title", "Branch", "Description", "Action"].map(
                      (col, j) => (
                        <TableCell key={`skeleton-${i}-${j}`}>
                          <Skeleton variant="text" />
                        </TableCell>
                      )
                    )}
                  </TableRow>
                ))
              : currencies.map((currency, i) => (
                  <TableRow key={currency.id} hover>
                    <TableCell>{(page - 1) * rowsPerPage + i + 1}</TableCell>
                    <TableCell>{currency.title}</TableCell>
                    <TableCell>{currency.shop_name || currency.shop?.name || currency.shop || "-"}</TableCell>
                    <TableCell>{currency.description || "-"}</TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ mr: 1 }}
                        onClick={() => {
                          setEditData(currency);
                          setOpenEdit(true);
                        }}
                      >
                        {t("currency.edit")}
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleDelete(currency.id)}
                      >
                        {t("currency.delete")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        padding="10px"
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Typography>{t("currency.rowsPerPage")}</Typography>
          <FormControl
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  border: "none",
                },
              },
            }}
            size="small"
          >
            <Select value={rowsPerPage} onChange={handleRowsPerPageChange}>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={30}>30</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Pagination
          count={Math.ceil(totalCount / rowsPerPage)}
          page={page}
          onChange={handlePageChange}
          variant="outlined"
          color="primary"
          sx={{ ml: 2 }}
        />
      </Grid>
      <ConfirmDialog />
      {openAdd && (
        <CurrencyAdd
          open={openAdd}
          onClose={() => setOpenAdd(false)}
          token={token}
        />
      )}
      {openEdit && (
        <CurrencyEdit
          open={openEdit}
          onClose={() => setOpenEdit(false)}
          editData={editData}
          token={token}
        />
      )}
    </Box>
  );
};

export default CurrencyIndex;
